import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, StatCard, StatusBadge, EmptyState, LoadingSkeleton } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  PackageCheck, PackagePlus, Truck, CheckCircle2, RotateCcw, Wallet, Plus, ChevronRight, AlertTriangle, Boxes,
  BadgeCheck, Circle,
} from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { can } from "@/lib/permissions";

const inr = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

// Status-split tiles -> orders tab group (contract tab->status map ids).
const STAT_TILES = [
  { key: "new", label: "New", icon: PackagePlus, accent: "neutral", group: "new" },
  { key: "ready", label: "Ready to Ship", icon: Boxes, accent: "info", group: "ready" },
  { key: "transit", label: "In Transit", icon: Truck, accent: "warning", group: "transit" },
  { key: "delivered", label: "Delivered", icon: CheckCircle2, accent: "success", group: "delivered" },
  { key: "rto", label: "RTO", icon: RotateCcw, accent: "danger", group: "rto" },
];

const B2CDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);
  const [enableMsg, setEnableMsg] = useState("");
  const [dash, setDash] = useState({ counts: {}, wallet_balance: 0, recent: [], kyc_status: null });

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/b2c/seller/dashboard`);
      setDash({
        counts: data.counts || {},
        wallet_balance: data.wallet_balance ?? 0,
        recent: Array.isArray(data.recent) ? data.recent : [],
        kyc_status: data.kyc_status ?? null,
      });
      setNotEnabled(false);
    } catch (e) {
      if (e?.response?.status === 422) {
        setNotEnabled(true);
        setEnableMsg(errDetail(e, "B2C is not enabled for your account yet."));
      } else {
        toast.error(errDetail(e, "Failed to load B2C dashboard"));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const canCreate = can(user, "b2c.orders:create");
  const goTab = (group) => navigate(`/seller/b2c/orders?status_group=${group}`);

  // ---- Not enabled / no plan: graceful onboarding hint ----
  if (notEnabled && !loading) {
    return (
      <SellerLayout user={user} onLogout={onLogout}>
        <div className="space-y-6">
          <PageHeader
            title="B2C Dashboard"
            subtitle="Last-mile shipping for your D2C orders"
            badge={
              <Badge className="bg-amber-100 text-amber-800 border-0 gap-1">
                <AlertTriangle className="w-3 h-3" /> Not enabled
              </Badge>
            }
          />
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              <EmptyState
                icon={PackageCheck}
                title="B2C shipping isn't enabled yet"
                description={
                  enableMsg ||
                  "Your account doesn't have a B2C plan configured. Ask your account manager to enable B2C shipping, then refresh this page."
                }
                action={
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button variant="outline" onClick={() => navigate("/seller/pickup-locations")}>
                      Add a pickup address
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/seller/wallet")}>
                      <Wallet className="w-4 h-4 mr-2" /> Recharge wallet
                    </Button>
                  </div>
                }
              />
            </CardContent>
          </Card>
        </div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <PageHeader
          title="B2C Dashboard"
          subtitle="Ship parcels to your buyers' doorsteps — COD & prepaid"
          actions={
            canCreate ? (
              <Button onClick={() => navigate("/seller/b2c/orders/create")} data-testid="b2c-dashboard-create">
                <Plus className="w-4 h-4 mr-2" /> Create B2C Order
              </Button>
            ) : undefined
          }
        />

        {/* Onboarding checklist — shown until KYC is approved (mirrors Shiprocket/iThink first-run) */}
        {!loading && dash.kyc_status && dash.kyc_status !== "approved" && (
          <Card className="border-0 shadow-sm border-l-4 border-l-amber-400" data-testid="b2c-onboarding-checklist">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-amber-500" /> Finish setup to start shipping
              </CardTitle>
              <CardDescription>Your first B2C shipment needs KYC approved and a funded wallet.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { label: "Verify your email", done: user?.email_verified !== false },
                {
                  label:
                    dash.kyc_status === "pending" ? "KYC submitted — under review"
                    : dash.kyc_status === "rejected" ? "KYC rejected — please resubmit"
                    : "Complete KYC verification (PAN)",
                  done: false,
                  ctaLabel: dash.kyc_status === "pending" ? "View status" : dash.kyc_status === "rejected" ? "Resubmit" : "Complete KYC",
                  to: "/seller/b2c/kyc",
                },
                { label: "Add wallet balance", done: dash.wallet_balance > 0, ctaLabel: "Recharge", to: "/seller/wallet" },
              ].map((step) => (
                <div key={step.label} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {step.done
                      ? <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                      : <Circle className="w-5 h-5 text-slate-300 shrink-0" />}
                    <span className={`text-sm ${step.done ? "text-slate-400 line-through" : "text-slate-800 font-medium"}`}>
                      {step.label}
                    </span>
                  </div>
                  {!step.done && step.to && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(step.to)}
                      data-testid={`onboard-cta-${step.to.split("/").pop()}`}
                    >
                      {step.ctaLabel} <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Wallet chip + recharge CTA */}
        <Card className="border-0 shadow-sm">
          <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between py-4">
            <div className="flex items-center gap-3">
              <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${dash.wallet_balance < 0 ? "bg-red-100 text-red-600" : "bg-brand/10 text-brand"}`}>
                <Wallet className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xs text-slate-500">Wallet balance (shared with B2B)</p>
                <p className={`text-xl font-heading font-bold tabular-nums ${dash.wallet_balance < 0 ? "text-red-600" : "text-slate-800"}`} data-testid="b2c-wallet-balance">
                  {loading ? "…" : inr(dash.wallet_balance)}
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => navigate("/seller/wallet")} data-testid="b2c-recharge">
              <Wallet className="w-4 h-4 mr-2" /> Recharge wallet
            </Button>
          </CardContent>
        </Card>

        {/* Status-split StatCards (click-through to filtered orders) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STAT_TILES.map((t) => (
            <StatCard
              key={t.key}
              label={t.label}
              value={loading ? "" : (dash.counts[t.key] ?? 0)}
              icon={t.icon}
              accent={t.accent}
              loading={loading}
              onClick={() => goTab(t.group)}
            />
          ))}
        </div>

        {/* Recent orders mini list */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between space-y-0">
            <div>
              <CardTitle className="text-lg">Recent B2C orders</CardTitle>
              <CardDescription>Your latest shipments.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate("/seller/b2c/orders")} data-testid="b2c-view-all-orders">
              View all <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <LoadingSkeleton variant="table" rows={4} cols={3} />
            ) : dash.recent.length === 0 ? (
              <EmptyState
                icon={PackageCheck}
                title="No B2C orders yet"
                description="Create your first order to ship a parcel to your buyer's doorstep."
                action={
                  canCreate ? (
                    <Button onClick={() => navigate("/seller/b2c/orders/create")} data-testid="b2c-recent-create">
                      <Plus className="w-4 h-4 mr-2" /> Create B2C Order
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <ul className="divide-y divide-slate-100">
                {dash.recent.map((o) => (
                  <li key={o.id}>
                    <button
                      onClick={() => navigate(`/seller/b2c/orders/${o.id}`)}
                      className="flex w-full items-center justify-between gap-3 py-3 text-left hover:bg-slate-50 rounded-md px-2 -mx-2"
                      data-testid={`b2c-recent-${o.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">
                          {o.order_number} <span className="font-normal text-slate-500">· {o.buyer?.name || "-"}</span>
                        </p>
                        <p className="text-xs text-slate-500">
                          {[o.buyer?.city, o.buyer?.pincode].filter(Boolean).join(", ")} · {formatToIST(o.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <span className="text-sm font-medium text-slate-700 tabular-nums">
                          {o.payment_mode === "cod" ? inr(o.cod_amount) : inr(o.order_value)}
                        </span>
                        <StatusBadge status={o.status} kind="b2c" size="sm" />
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </SellerLayout>
  );
};

export default B2CDashboard;
